import { Injectable } from '@angular/core';
import { Validators } from '@angular/forms';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { DsServersComponent } from 'src/app/ds/component/server-list/server-list.component';
import { ChartDialogComponent } from 'src/app/shared/component/dialog/chart-dialog/chart-dialog.component';
import { ExportDataComponent } from 'src/app/shared/component/dialog/export-data/export-data-component';
import {
    TextEditorDialogComponent,
    TextEditorDialogOutput
} from 'src/app/shared/component/dialog/text-editor-dialog/text-editor-dialog.component';
import { FileType } from 'src/app/shared/model/file-type';
import { FormFieldElement, FormFieldType } from 'src/app/shared/model/form-field-element';
import { ImageViewerInput } from 'src/app/shared/model/image-vewer-input';
import { RequestCriteria } from 'src/app/shared/model/request-criteria';
import { ViewState } from 'src/app/shared/model/view-state';
import { InputDialogData } from '../model/input-dialog-data';
import { ConfirmationDialogComponent } from './confirmation/confirmation-dialog.component';
import { DialogOption, OptionsDialogComponent } from './options/options-dialog.component';
import { ImageViewerDialogComponent } from './image-viewer-dialog/image-viewer-dialog.component';
import { InfoDialogComponent } from './info-dialog/info-dialog.component';
import { InputDialogComponent } from './input-dialog/input-dialog.component';
import { LoadingDialogComponent } from './loading-dialog/loading-dialog.component';
import { TextConfirmationComponent } from './text-confirmation/text-confirmation.component';

@Injectable()
export class DialogService {
    constructor(private dialog: MatDialog) { }

    openConfirmationDialog(
        titleKey: string = 'confirmation',
        messageKey: string = 'areYouSure',
        confirmationKey: string = 'yes',
        cancelKey: string = 'cancel',
        disableClose: boolean = false,
        messageParams = {},
        width: string = '450px',
        titleName: string = null
    ): MatDialogRef<ConfirmationDialogComponent, boolean | null> {
        return this.dialog.open(ConfirmationDialogComponent, {
            width,
            data: {
                title: titleKey,
                message: messageKey,
                confirmText: confirmationKey,
                cancelText: cancelKey,
                disableClose,
                messageParams,
                titleName
            },
            autoFocus: false
        });
    }

    openOptionsDialog(
        titleKey: string,
        messageKey: string,
        options: DialogOption[],
        messageParams = {},
        width: string = '450px'
    ): MatDialogRef<OptionsDialogComponent, string> {
        return this.dialog.open(OptionsDialogComponent, {
            width,
            data: {
                title: titleKey,
                message: messageKey,
                options,
                messageParams
            },
            autoFocus: false
        });
    }

    openInfoDialog(titleKey: string = 'information', messageKey: string, closeKey: string = 'ok'): MatDialogRef<InfoDialogComponent, any> {
        return this.dialog.open(InfoDialogComponent, {
            width: '450px',
            data: {
                title: titleKey,
                message: messageKey,
                closeText: closeKey
            },
            autoFocus: false
        });
    }

    openErrorDialog(messageKey: string): MatDialogRef<InfoDialogComponent, any> {
        return this.openInfoDialog('error', messageKey, 'ok');
    }

    showLoadingDialog(message: string = null): MatDialogRef<LoadingDialogComponent, any> {
        return this.dialog.open(LoadingDialogComponent, {
            disableClose: true,
            data: {
                message
            }
        });
    }

    openInputDialog(
        formFieldElement: FormFieldElement,
        titleKey: string,
        messageKey: string,
        confirmationKey: string = 'ok',
        cancelKey: string = 'cancel'
    ): MatDialogRef<InputDialogComponent, any> {
        const inputDialogData: InputDialogData = {
            title: titleKey,
            message: messageKey,
            confirmText: confirmationKey,
            cancelText: cancelKey,
            formFieldElement
        };
        return this.dialog.open(InputDialogComponent, {
            data: inputDialogData
        });
    }

    openChartDialog(fileId: number, chartType: string, filters: RequestCriteria, viewState: ViewState): void {
        this.dialog.open(ChartDialogComponent, {
            data: { fileId, chartType, filters, viewState },
            minHeight: 'calc(100vh - 60px)',
            minWidth: 'calc(100vw - 60px)'
        });
    }

    openTextEditorDialog(textContent: TextEditorDialogOutput | null): MatDialogRef<TextEditorDialogComponent> {
        return this.dialog.open(TextEditorDialogComponent, { data: textContent });
    }

    openDataExportDialog(title: string, isSchedule: boolean, dataTypes: FileType[]): MatDialogRef<ExportDataComponent> {
        return this.dialog.open(ExportDataComponent, {
            data: {
                exportDataTypes: dataTypes,
                isSchedule,
                title
            }
        });
    }

    openImageViever(input: ImageViewerInput): MatDialogRef<ImageViewerDialogComponent> {
        return this.dialog.open(ImageViewerDialogComponent, { width: '80vw', height: '80vh', data: input });
    }

    openDSServerChooseDialog(): void {
        const component = this.dialog.open(DsServersComponent);
        component.componentInstance.emitWhenSingle = true;
        component.componentInstance.serverSelected.subscribe(server => {
            window.open(`http://${server.value}`, '_blank');
            setTimeout(() => component.close(), 10);
        });
    }

    openLengthInputDialog(defaultValue: number = null): MatDialogRef<InputDialogComponent, any> {
        return this.openInputDialog({
            name: 'length',
            textName: 'lengthInSeconds',
            defaultValue,
            fieldType: FormFieldType.INPUT,
            min: 1,
            max: 3600,
            step: 1,
            formFieldType: 'number',
            validators: [
                {
                    validator: Validators.required,
                    validatorErrorName: 'required',
                    errorKey: 'required'
                },
                {
                    validator: Validators.pattern(/^([0-9]+)$/),
                    validatorErrorName: 'pattern',
                    errorKey: 'valueOnlyNumeric'
                },
                {
                    validator: Validators.min(1),
                    validatorErrorName: 'min',
                    errorKey: 'validRange',
                    errorParams: { min: 1, max: 3600 }
                },
                {
                    validator: Validators.max(3600),
                    validatorErrorName: 'max',
                    errorKey: 'validRange',
                    errorParams: { min: 1, max: 3600 }
                }
            ]
        }, 'enterValue', 'enterImageFileAdLength', 'save');
    }

    openTextConfirmationDialog(infoText: string, wordToRepeat: string): MatDialogRef<TextConfirmationComponent> {
        return this.dialog.open(TextConfirmationComponent, { data: { infoText, wordToRepeat }, minWidth: '320px', maxWidth: '50vw' });
    }


}
